import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import VideoCameraFrontRoundedIcon from '@mui/icons-material/VideoCameraFrontRounded'
import { Avatar, Chip } from '@mui/material'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import { CandidateMeQueryQuery } from '__generated__/graphql'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import SchedulingBanner from 'candidate/components/SchedulingBanner'
import { StyledContainer } from 'candidate/components/styles'
import {
    CallScheduledInviteeAPI,
    CandidateMeQuery,
    CreateCandidateSchedule,
    GetCandidateSchedule,
    GetEventDetails,
} from 'candidate/graphql/candidate.gql'
import React, { useCallback, useState } from 'react'
import { ReactComponent as EmptySchedule } from 'shared/assets/new_empty_schedule.svg'
import { useCalendlyEventListener } from 'shared/components/Calendly'
import useToast from 'shared/components/Toast/useToast'
import {
    StyledCard,
    StyledEmptyCardContent,
    TitleContainer,
} from 'shared/style/styled'
import theme from 'shared/style/theme'
import { getPxAsRem } from 'shared/utils'
import { formatDateForSchedule } from 'shared/utils/dateFormatter'
import {
    CancelButton,
    RescheduleButton,
    ScheduleItem,
    ScheduleList,
} from './styled'

interface ScheduleProps {
    schedules: {
        id: string
        candidateId: string
        eventUrl: string
        event: any
        invitee: any
        inviteeUrl: string
        createdAt: string
        updatedAt: string
    }[]
}

const Schedule: React.FC = () => {
    usePageTitle('Schedule')
    const [schedules, setSchedules] = useState<ScheduleProps['schedules']>([])
    const [candidate, setCandidate] =
        useState<CandidateMeQueryQuery['candidateMe']>()
    const [loading, setLoading] = useState(false)
    const toast = useToast()
    const [fetchEventDetails] = useLazyQuery(GetEventDetails)
    const [fetchInviteeDetails] = useLazyQuery(CallScheduledInviteeAPI)
    useQuery(CandidateMeQuery, {
        fetchPolicy: 'network-only',
        onCompleted(data) {
            setCandidate(data?.candidateMe)
        },
        onError(error) {
            console.log('error with CandidateMeQuery', error)
        },
    })

    useQuery(GetCandidateSchedule, {
        variables: { id: candidate?.id || '' },
        skip: !candidate?.id,
        fetchPolicy: 'network-only',
        onCompleted(data) {
            fetchScheduleDetails(data.candidateSchedule)
        },
        onError(error) {
            console.log('error with GetCandidateSchedule query', error)
            toast({
                severity: 'error',
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        },
    })

    const [submitSchedule] = useMutation(CreateCandidateSchedule, {
        onCompleted(data) {
            console.log('schedule submitted: ', data)
            toast({
                severity: 'success',
                title: 'Success!',
                message: 'Meeting was scheduled successfully!',
            })
        },
        onError(error) {
            console.error('schedule submission error: ', error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        },
    })

    const persistBooking = useCallback(
        (eventData: any) => {
            const input = {
                candidateId: candidate?.id,
                eventUrl: eventData.event.uri,
                inviteeUrl: eventData.invitee.uri,
            }
            submitSchedule({
                variables: { input },
            })
        },
        [candidate?.id, submitSchedule]
    )

    useCalendlyEventListener({
        onEventScheduled: (e) => {
            persistBooking(e.data.payload)
        },
    })

    const fetchScheduleDetails = useCallback(
        async (schedules) => {
            if (!schedules || schedules.length === 0) return

            try {
                setLoading(true)
                const eventPromises = schedules.map((schedule) =>
                    fetchEventDetails({
                        variables: { eventId: schedule.eventUrl },
                        fetchPolicy: 'cache-first',
                    })
                )

                const inviteePromises = schedules.map((schedule) =>
                    fetchInviteeDetails({
                        variables: { inviteeId: schedule.inviteeUrl },
                        fetchPolicy: 'cache-first',
                    })
                )

                const eventResults = await Promise.all(eventPromises)
                const inviteeResults = await Promise.all(inviteePromises)

                const fetchedEventsData = eventResults.map(
                    (result) => result.data.callScheduledEventsAPI
                )
                const fetchedInviteesData = inviteeResults.map(
                    (result) => result.data.callScheduledInviteeAPI
                )

                let combined = schedules.map((schedule, index) => ({
                    ...schedule,
                    event: fetchedEventsData[index],
                    invitee: fetchedInviteesData[index],
                }))
                combined = combined.sort(
                    (a, b) =>
                        new Date(a.createdAt).getTime() -
                        new Date(b.createdAt).getTime()
                )
                setSchedules(combined)
            } catch (error) {
                console.error('Error fetching details:', error)
                toast({
                    severity: 'error',
                    title: 'Something went wrong',
                    message: 'Please try again later.',
                })
            } finally {
                setLoading(false)
            }
        },
        [fetchEventDetails, fetchInviteeDetails, toast]
    )

    const activeSchedules = schedules.filter(
        (schedule) => schedule.event?.status === 'active'
    )

    return (
        <StyledContainer>
            <TitleContainer>
                <Typography variant="h2">Schedule</Typography>
            </TitleContainer>

            <SchedulingBanner />

            {loading ? (
                <Typography>Loading...</Typography>
            ) : activeSchedules.length === 0 ? (
                <StyledCard>
                    <StyledEmptyCardContent>
                        <EmptySchedule />
                        <Typography variant="body1">
                            You currently have no scheduled meetings.
                        </Typography>
                    </StyledEmptyCardContent>
                </StyledCard>
            ) : (
                <ScheduleList>
                    {activeSchedules.map(
                        (schedule) =>
                            schedule.event?.status === 'active' && (
                                <ScheduleItem key={schedule.id}>
                                    <Card sx={{ width: 363 }}>
                                        <CardContent>
                                            <Typography
                                                variant="h3"
                                                color={
                                                    theme.palette.primary.main
                                                }
                                                sx={{ fontSize: 14 }}
                                            >
                                                {formatDateForSchedule(
                                                    schedule.event?.start_time,
                                                    schedule.event?.end_time
                                                )}
                                            </Typography>
                                            <Typography
                                                variant="h5"
                                                component="div"
                                            >
                                                {schedule.event?.name}
                                            </Typography>
                                            {schedule.event?.event_memberships.map(
                                                (member: any) => (
                                                    <Chip
                                                        style={{
                                                            marginTop:
                                                                getPxAsRem(20),
                                                        }}
                                                        key={member.user_name}
                                                        avatar={
                                                            <Avatar>
                                                                {
                                                                    member
                                                                        .user_name[0]
                                                                }
                                                            </Avatar>
                                                        }
                                                        label={member.user_name}
                                                    />
                                                )
                                            )}
                                        </CardContent>
                                        <CardActions>
                                            <CancelButton
                                                url={
                                                    schedule.invitee?.cancel_url
                                                }
                                                rootElement={
                                                    document.getElementById(
                                                        'root'
                                                    )!
                                                }
                                                text="CANCEL"
                                            />
                                            <RescheduleButton
                                                url={
                                                    schedule.invitee
                                                        ?.reschedule_url
                                                }
                                                rootElement={
                                                    document.getElementById(
                                                        'root'
                                                    )!
                                                }
                                                text="RESCHEDULE"
                                            />
                                            <div
                                                aria-label="meeting link"
                                                onClick={() =>
                                                    schedule.event?.location
                                                        ?.join_url.length > 0 &&
                                                    window.open(
                                                        schedule.event?.location
                                                            ?.join_url,
                                                        '_blank'
                                                    )
                                                }
                                            >
                                                <VideoCameraFrontRoundedIcon
                                                    sx={{
                                                        fontSize:
                                                            getPxAsRem(36),
                                                        marginTop:
                                                            getPxAsRem(5),
                                                        color: theme.palette
                                                            .primary.main,
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            </div>
                                        </CardActions>
                                    </Card>
                                </ScheduleItem>
                            )
                    )}
                </ScheduleList>
            )}
        </StyledContainer>
    )
}

export default Schedule
